/**
 * E002-T018: DatabaseDiscoveryService
 * E012-T018: RBAC Database Visibility Filter
 * E018-S012-T002: Extends BlockDiscoveryService
 * S020-8.20.4: Write Permission Filtering for Output Tab
 *
 * Findet alle Databases im aktuellen Workspace.
 * Extends BlockDiscoveryService for shared workspace-wide scanning logic.
 *
 * Reaktivität:
 * - Hört auf docs$ für neue/gelöschte Docs (AFFiNE)
 * - Hört auf blockSuiteDoc.slots.blockUpdated für Block-Änderungen (BlockSuite)
 *
 * RBAC (E012-T018):
 * - accessibleDatabases$ filtert nach Doc_Read Permission via GuardService
 * - writableDatabases$ filtert nach Doc_Update Permission (S020-8.20.4)
 * - UI-Komponenten verwenden gefilterte Liste für Security
 */

import { databaseDeleteDependenciesQuery } from '@affine/graphql';
import { DatabasePublicationBaseUnavailable } from '@blocksuite/affine/blocks/database';
import type { Block, Store } from '@blocksuite/affine/store';
import {
  isDatabaseStorageModelReady,
  observeDatabaseStorageModelReady,
  resolveDatabaseStorageModel,
} from '@blocksuite/affine-block-database';
import {
  getSystemWorkspaceStore,
  peekSystemWorkspaceStore,
} from '@blocksuite/affine-block-streamlain';
import { DatabaseBlockModel } from '@blocksuite/affine-model';
import { parseXYWH } from '@blocksuite/global/gfx';
import {
  type DatabaseShardDirectoryHost,
  isHiddenSystemManagedDatabase,
  isValidDatabaseBlockId,
  readDatabaseShardDirectoryDocIds,
  readDatabaseShardDirectoryProps,
} from '@streamlain/database-identity';
import { SL_VIEW_ROLE_KEY } from '@streamlain/view-config';
import { LiveData } from '@toeverything/infra';
import { combineLatest, distinctUntilChanged, map } from 'rxjs';

import type { WorkspaceServerService } from '../../cloud';
import type { GraphQLService } from '../../cloud/services/graphql';
import type { DocRecord, DocsService } from '../../doc';
import { preloadDatabaseSubdocsForDataSourceRefs } from '../../doc/services/database-subdoc-preload';
import {
  flushDatabaseSystemDocsForDurableWrite,
  type ResolvedDatabaseSystemDocBinding,
} from '../../doc/services/database-system-doc-access';
import type { DocsSearchService } from '../../docs-search';
import type { GuardService } from '../../permissions';
import type { WorkspaceService } from '../../workspace';
import { parseXywhToPosition } from '../edge-infrastructure/core/constants';
import {
  BlockDiscoveryService,
  type BlockInfo,
  type TrashcanItem,
} from './block-discovery';
import {
  areDatabaseCatalogsEqual,
  mergeDatabaseCatalog,
  sortDatabaseCatalog,
  watchPermittedDatabases,
} from './database-catalog';
import {
  type DatabaseDeleteDependencies,
  mapDatabaseDeleteDependencies,
} from './database-relation-dependencies';

/**
 * Database-specific doc group with `databases` field for backward compatibility.
 */
export interface DatabaseDocGroup {
  docId: string;
  docTitle: string;
  databases: DatabaseInfo[];
}

/**
 * Column-Info einer Database
 */
export interface ColumnInfo {
  id: string;
  name: string;
  type: string;
  /** For relation columns: target database ID */
  targetDatabaseId?: string;
  /** For relation columns: target doc ID (for CrossDoc relations) */
  targetDocId?: string;
  /** For relation columns: cardinality ('one' or 'many') */
  cardinality?: 'one' | 'many';
  /** For relation columns: inverse cardinality ('one' or 'many') — sl-tah4.4 */
  inverseCardinality?: 'one' | 'many';
  /** sl-vb5r: Raw column data (select options, etc.) */
  data?: Record<string, unknown>;
}

/**
 * Info über eine gefundene Database
 */
export interface DatabaseInfo extends BlockInfo {
  /** Index-only entry: resolve schema only when a consumer selects this table. */
  schemaLoaded?: boolean;
  /** Titel der Database */
  databaseTitle: string;
  /** Canvas visibility flag for system-managed databases such as File Index. */
  visible?: boolean;
  /** Storage root document for sharded database schema/manifest data. */
  databaseRootDocId?: string;
  /** Directory part documents holding the row-shard list. */
  databaseDirectoryPartDocIds?: string[];
  /** Row-shard documents of the loaded directory parts. */
  databaseRowShardDocIds?: string[];
  /** Spalten der Database */
  columns: ColumnInfo[];
  /** true = column/relation schema is managed by a feature and must not be user-deleted casually */
  schemaLocked?: boolean;
  /** true = user row/schema writes are generally managed by a feature */
  systemManaged?: boolean;
  /** Feature owner marker, e.g. "view-config" or "app-base-standard:content" */
  managedBy?: string;
  /** Normalized StreamLain role marker stored on the database block. */
  streamlainRole?: string;
  /** Role a managing feature gave the table, e.g. a mailbox's "folder_view". */
  tableRole?: string;
}

export function isUserFacingWorkspaceDatabase(database: DatabaseInfo): boolean {
  return (
    isValidDatabaseBlockId(database.blockId) &&
    !isHiddenSystemManagedDatabase({
      id: database.blockId,
      managedBy: database.managedBy,
      visible: database.visible,
    })
  );
}

export function isHiddenSystemManagedWorkspaceDatabase(
  database: DatabaseInfo
): boolean {
  return isHiddenSystemManagedDatabase({
    id: database.blockId,
    managedBy: database.managedBy,
    visible: database.visible,
  });
}

export function isHiddenFileIndexWorkspaceDatabase(
  database: DatabaseInfo
): boolean {
  return isHiddenSystemManagedWorkspaceDatabase(database);
}

export function isAccessibleWorkspaceDatabase(database: DatabaseInfo): boolean {
  return (
    !database.isInTrash && !isHiddenSystemManagedWorkspaceDatabase(database)
  );
}

export function isUserFacingActiveWorkspaceDatabase(
  database: DatabaseInfo
): boolean {
  return (
    isUserFacingWorkspaceDatabase(database) &&
    database.isInTrash !== true &&
    typeof database.databaseRootDocId === 'string' &&
    database.databaseRootDocId.trim().length > 0
  );
}

function getNonEmptyText(value: unknown): string | undefined {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }

  if (
    value &&
    typeof value === 'object' &&
    'toString' in value &&
    typeof value.toString === 'function'
  ) {
    const text = value.toString().trim();
    return text.length > 0 && text !== '[object Object]' ? text : undefined;
  }

  return undefined;
}

type StorageColumnProps = {
  id: string;
  name: string;
  type: string;
  data?: {
    targetDatabaseId?: string;
    targetDocId?: string;
    cardinality?: 'one' | 'many';
    inverseCardinality?: 'one' | 'many';
  };
};

type StorageModelProps = {
  title?: { toString(): string };
  columns?: StorageColumnProps[];
  schemaLocked?: boolean;
  systemManaged?: boolean;
  managedBy?: string;
  visible?: boolean;
  databaseShardManifest?: unknown;
  [SL_VIEW_ROLE_KEY]?: string;
};

type TrashcanSnapshotProps = {
  schemaLocked?: unknown;
  systemManaged?: unknown;
  managedBy?: unknown;
  title?: unknown;
  databaseTitle?: unknown;
  name?: unknown;
  xywh?: string;
  databaseId?: unknown;
  databaseRootDocId?: unknown;
};

function readTrashcanSnapshotProps(
  item: TrashcanItem
): TrashcanSnapshotProps | undefined {
  return (
    item as TrashcanItem & {
      snapshot?: { content?: Array<{ props?: TrashcanSnapshotProps }> };
    }
  ).snapshot?.content?.[0]?.props;
}

/**
 * Sharded storage identity of a trashed database. Items written since the
 * identity fields exist carry them directly; older items fall back to the
 * host block props captured in the snapshot.
 */
export function readTrashcanDatabaseIdentity(item: TrashcanItem): {
  databaseId: string;
  databaseRootDocId?: string;
} {
  const snapshotProps = readTrashcanSnapshotProps(item);
  const databaseId =
    getNonEmptyText(item.databaseId) ??
    getNonEmptyText(snapshotProps?.databaseId) ??
    item.id;
  const databaseRootDocId =
    getNonEmptyText(item.databaseRootDocId) ??
    getNonEmptyText(snapshotProps?.databaseRootDocId);
  return { databaseId, databaseRootDocId };
}

/**
 * Title shown for a trashed database. The live storage title from the root
 * system document survives host-block deletion and reflects renames.
 * Call only after the canonical root has finished loading.
 */
export function resolveTrashcanDatabaseTitle(liveTitle?: string): string {
  return getNonEmptyText(liveTitle) ?? 'Untitled';
}

/**
 * Map storage columns to discovery columns, prepending the built-in title
 * column when the storage model does not list it explicitly.
 */
function mapStorageColumns(
  rawColumns: StorageColumnProps[] | undefined
): ColumnInfo[] {
  const mappedColumns: ColumnInfo[] = (rawColumns ?? []).map(col => ({
    id: col.id,
    name: col.name,
    type: col.type,
    targetDatabaseId: col.data?.targetDatabaseId,
    targetDocId: col.data?.targetDocId,
    cardinality: col.data?.cardinality,
    inverseCardinality: col.data?.inverseCardinality,
    data: col.data,
  }));
  const hasTitleColumn = mappedColumns.some(col => col.type === 'title');
  return hasTitleColumn
    ? mappedColumns
    : [{ id: 'title', name: 'Title', type: 'title' }, ...mappedColumns];
}

function readTrashedStorageModelProps(
  hostStore: Store,
  identity: { databaseId: string; databaseRootDocId?: string }
): StorageModelProps | undefined {
  const rootDocId = identity.databaseRootDocId;
  if (!rootDocId || rootDocId === hostStore.id) return undefined;
  const rootStore = peekSystemWorkspaceStore(hostStore, rootDocId);
  const storageModel = rootStore?.getBlock(identity.databaseId)?.model;
  if (!storageModel || storageModel.flavour !== 'affine:database') {
    return undefined;
  }
  return storageModel.props as StorageModelProps;
}

export class DatabaseDiscoveryService extends BlockDiscoveryService<DatabaseInfo> {
  // Retain only the explicitly referenced trash roots while their host is in
  // the working set. This avoids repeatedly opening/releasing a system doc on
  // every discovery refresh and also observes remote root-title changes.
  private readonly trashRoots = new Map<
    string,
    {
      hostId: string;
      blockId: string;
      databaseId: string;
      rootDocId: string;
      ready: Promise<unknown>;
      release: () => void;
    }
  >();
  constructor(
    docsService: DocsService,
    private readonly guardService: GuardService,
    private readonly workspaceService: WorkspaceService,
    private readonly workspaceServerService: WorkspaceServerService,
    private readonly docsSearchService: DocsSearchService
  ) {
    super(docsService);
  }

  private requireGraphQL(): GraphQLService['gql'] {
    const gql = this.workspaceServerService.server?.gql ?? null;
    if (!gql) {
      throw new Error(
        'GraphQL service not available - workspace not connected to server'
      );
    }
    return gql;
  }

  // ============================================================
  // Abstract Properties Implementation
  // ============================================================

  readonly blockFlavour = 'affine:database';
  readonly trashcanBlockType = 'database';

  // ============================================================
  // Abstract Methods Implementation
  // ============================================================

  extractBlockInfo(block: Block, docRecord: DocRecord): DatabaseInfo | null {
    const blockId = block.model?.id ?? block.id;
    if (!isValidDatabaseBlockId(blockId)) {
      console.warn(
        '[DatabaseDiscovery] Skipping invalid database block id:',
        blockId
      );
      return null;
    }

    const hostProps = block.model?.props as
      | {
          title?: { toString(): string };
          xywh?: string;
        }
      | undefined;
    // Discovery only needs the database's schema metadata (title, columns,
    // manifest, flags). Resolve the storage model directly instead of building
    // a DatabaseBlockDataSource. This keeps the sidebar/discovery path on the
    // pure root-doc metadata contract and prevents future DataSource row-cache
    // lifecycle changes from accidentally turning discovery into a row-shard
    // observer owner again.
    const storageModel = block.model
      ? resolveDatabaseStorageModel(block.model as any)
      : null;
    const props = (storageModel as any)?.props as StorageModelProps | undefined;
    const blockProps = (block.model?.props ?? {}) as Record<string, unknown>;
    const databaseRootDocId =
      typeof blockProps.databaseRootDocId === 'string'
        ? blockProps.databaseRootDocId
        : undefined;
    // The storage model on the root document stores the directory.
    const directoryDocIds = this.readDirectoryDocIds(storageModel);

    // Handle empty string as well as undefined/null
    const rawTitle = props?.title?.toString();
    const title = rawTitle && rawTitle.trim() ? rawTitle : 'Untitled';

    const columns = mapStorageColumns(props?.columns);

    return {
      blockId,
      docId: docRecord.id,
      docTitle: docRecord.title$.value || 'Untitled',
      databaseTitle: title,
      visible:
        typeof blockProps.visible === 'boolean'
          ? blockProps.visible
          : typeof props?.visible === 'boolean'
            ? props.visible
            : undefined,
      databaseRootDocId,
      databaseDirectoryPartDocIds: directoryDocIds.partDocIds,
      databaseRowShardDocIds: directoryDocIds.shardDocIds,
      columns,
      xywh: hostProps?.xywh,
      isInTrash: false,
      schemaLocked: props?.schemaLocked === true,
      systemManaged: props?.systemManaged === true,
      managedBy:
        typeof props?.managedBy === 'string'
          ? props.managedBy
          : typeof blockProps.managedBy === 'string'
            ? blockProps.managedBy
            : undefined,
      streamlainRole:
        typeof props?.[SL_VIEW_ROLE_KEY] === 'string'
          ? props[SL_VIEW_ROLE_KEY]
          : typeof blockProps[SL_VIEW_ROLE_KEY] === 'string'
            ? (blockProps[SL_VIEW_ROLE_KEY] as string)
            : undefined,
      tableRole:
        typeof blockProps.appxTableRole === 'string'
          ? blockProps.appxTableRole
          : undefined,
    };
  }

  override async extractTrashcanInfo(
    item: TrashcanItem,
    docRecord: DocRecord,
    hostStore: Store
  ): Promise<DatabaseInfo | null> {
    const identity = readTrashcanDatabaseIdentity(item);
    const rootDocId = identity.databaseRootDocId;
    if (!rootDocId) throw new Error('Trashed database has no root identity.');
    const key = `${hostStore.id}\u0000${rootDocId}`;
    let retained = this.trashRoots.get(key);
    if (!retained) {
      const rootStore = getSystemWorkspaceStore(hostStore, rootDocId);
      // Root documents are intentionally absent from the visible DocRecordList.
      // Retain just their metadata through the canonical system-document port.
      const releaseRoot = preloadDatabaseSubdocsForDataSourceRefs(
        [{ databaseRootDocId: rootDocId }],
        this.workspaceService
      );
      const abort = new AbortController();
      let released = false;
      const subscription = rootStore.slots.blockUpdated.subscribe(() =>
        this.refresh()
      );
      retained = {
        hostId: hostStore.id,
        blockId: item.id,
        databaseId: identity.databaseId,
        rootDocId,
        ready: Promise.resolve().then(async () => {
          const timeout = setTimeout(() => abort.abort(), 15_000);
          try {
            abort.signal.throwIfAborted();
            rootStore.load();
            await this.workspaceService.workspace.engine.doc.waitForDocReady(
              rootDocId,
              abort.signal
            );
            abort.signal.throwIfAborted();
          } finally {
            clearTimeout(timeout);
          }
        }),
        release: () => {
          if (released) return;
          released = true;
          abort.abort();
          subscription.unsubscribe();
          releaseRoot();
        },
      };
      this.trashRoots.set(key, retained);
    }
    try {
      await retained.ready;
    } catch (error) {
      retained.release();
      // A removed/re-added host may already have installed a fresh lease.
      if (this.trashRoots.get(key) === retained) this.trashRoots.delete(key);
      throw error;
    }
    if (this.trashRoots.get(key) !== retained) return null;
    const storageProps = readTrashedStorageModelProps(hostStore, identity);
    if (!storageProps) throw new Error('Trashed database root is unavailable.');
    const snapshotProps = readTrashcanSnapshotProps(item);
    const directoryDocIds = this.readDirectoryDocIds({
      props: storageProps,
      store: hostStore,
    });

    return {
      blockId: item.id,
      docId: docRecord.id,
      docTitle: docRecord.title$.value || 'Untitled',
      databaseTitle: resolveTrashcanDatabaseTitle(
        storageProps?.title?.toString()
      ),
      databaseRootDocId: rootDocId,
      databaseDirectoryPartDocIds: directoryDocIds.partDocIds,
      databaseRowShardDocIds: directoryDocIds.shardDocIds,
      columns: storageProps ? mapStorageColumns(storageProps.columns) : [],
      xywh: readTrashcanSnapshotProps(item)?.xywh,
      isInTrash: true,
      schemaLocked:
        (storageProps?.schemaLocked ?? snapshotProps?.schemaLocked) === true,
      systemManaged:
        (storageProps?.systemManaged ?? snapshotProps?.systemManaged) === true,
      managedBy:
        typeof storageProps?.managedBy === 'string'
          ? storageProps.managedBy
          : typeof snapshotProps?.managedBy === 'string'
            ? snapshotProps.managedBy
            : undefined,
    };
  }

  protected override reconcileTrashcanItems(
    items: TrashcanItem[],
    hostStore: Store
  ): void {
    const roots = new Set(
      items
        .filter(item => item.blockType === 'database')
        .map(item => readTrashcanDatabaseIdentity(item).databaseRootDocId)
    );
    for (const [key, retained] of this.trashRoots) {
      if (
        retained.hostId !== hostStore.id ||
        roots.has(key.slice(hostStore.id.length + 1))
      )
        continue;
      retained.release();
      this.trashRoots.delete(key);
    }
  }

  protected override unsubscribeFromDoc(docId: string): void {
    super.unsubscribeFromDoc(docId);
    for (const [key, retained] of this.trashRoots) {
      if (retained.hostId !== docId) continue;
      retained.release();
      this.trashRoots.delete(key);
    }
  }

  override dispose(): void {
    for (const retained of this.trashRoots.values()) retained.release();
    this.trashRoots.clear();
    super.dispose();
  }

  /**
   * Relation counterparts that break when the database is trashed or
   * permanently deleted, split by whether the current user can clean them up.
   */
  async resolveDeleteDependencies(
    databaseBlockId: string,
    docId: string
  ): Promise<DatabaseDeleteDependencies> {
    const workspace = this.workspaceService.workspace;
    const hostDoc = workspace.docCollection.getDoc(docId);
    if (!hostDoc) throw new DatabasePublicationBaseUnavailable();
    if (!hostDoc.ready) hostDoc.load();
    const host = hostDoc.getStore({ id: docId });
    const retained = [...this.trashRoots.values()].find(
      root => root.hostId === docId && root.blockId === databaseBlockId
    );
    const resolvedBindings: ResolvedDatabaseSystemDocBinding[] = [];
    const liveByHost = new Map<string, Set<string>>();
    let props: StorageModelProps | undefined;
    if (retained) {
      await retained.ready;
      props = readTrashedStorageModelProps(host, {
        databaseId: retained.databaseId,
        databaseRootDocId: retained.rootDocId,
      });
      const manifest = readDatabaseShardDirectoryProps(
        props,
        workspace.docCollection
      );
      if (!props || !manifest) throw new DatabasePublicationBaseUnavailable();
      resolvedBindings.push({
        databaseId: retained.databaseId,
        hostDocId: docId,
        rootDocId: retained.rootDocId,
        blockId: databaseBlockId,
        manifest,
      });
    } else {
      const block = host.getBlock(databaseBlockId)?.model;
      if (!(block instanceof DatabaseBlockModel))
        throw new DatabasePublicationBaseUnavailable();
      props = resolveDatabaseStorageModel(block).props as StorageModelProps;
      liveByHost.set(docId, new Set([databaseBlockId]));
    }
    for (const column of props.columns ?? []) {
      if (column.type !== 'relation' || !column.data?.targetDatabaseId)
        continue;
      const targetHost = column.data.targetDocId || docId;
      let ids = liveByHost.get(targetHost);
      if (!ids) liveByHost.set(targetHost, (ids = new Set()));
      ids.add(column.data.targetDatabaseId);
    }
    await flushDatabaseSystemDocsForDurableWrite({
      collection: workspace.docCollection,
      docEngine: workspace.engine.doc,
      docId,
      workspaceId: workspace.id,
      resolvedBindings,
      liveBindings: [...liveByHost].map(([hostDocId, ids]) => ({
        hostDocId,
        databaseIds: [...ids],
      })),
    });
    const result = await this.requireGraphQL()({
      query: databaseDeleteDependenciesQuery,
      variables: {
        input: { workspaceId: workspace.id, docId, blockId: databaseBlockId },
      },
    });
    return mapDatabaseDeleteDependencies(
      databaseBlockId,
      this.blocks$.value.find(database => database.blockId === databaseBlockId),
      result.databaseDeleteDependencies
    );
  }

  // ============================================================
  // Backward-Compatible Aliases
  // ============================================================

  /**
   * Workspace metadata index plus authoritative, completed local scans.
   */
  readonly databases$ = LiveData.from(
    combineLatest([
      this.blockCatalog$,
      this.docsService.list.docs$,
      this.docsSearchService.watchDatabaseBlocks(),
    ]).pipe(
      map(([catalog, docs, indexedBlocks]) =>
        mergeDatabaseCatalog(
          indexedBlocks,
          catalog.blocks,
          catalog.scannedDocIds,
          new Map(
            docs.map(doc => [
              doc.id,
              {
                title: doc.title$.value || 'Untitled',
                trashed: doc.trash$.value,
              },
            ])
          )
        )
      ),
      distinctUntilChanged<DatabaseInfo[]>(areDatabaseCatalogsEqual)
    ),
    []
  );

  /** Retain only an explicitly opened picker group; hydrate schema metadata, never rows. */
  acquireDatabaseGroup(docId: string, permission: 'read' | 'write') {
    let closed = false;
    const controller = new AbortController();
    let releaseDoc = () => {};
    const release = () => {
      closed = true;
      controller.abort();
      releaseDoc();
      releaseDoc = () => {};
    };
    const ready = (async () => {
      if (
        !(await this.guardService.can('Doc_Read', docId)) ||
        (permission === 'write' &&
          !(await this.guardService.can('Doc_Update', docId)))
      ) {
        throw new Error('Database access is no longer available.');
      }
      if (closed) return;
      const opened = this.docsService.open(docId);
      releaseDoc = opened.release;
      await opened.doc.waitForSyncReady();
      if (closed) return;
      const record = this.docsService.list.doc$(docId).value;
      if (!record || record.trash$.value)
        throw new Error('Database document is unavailable.');
      for (const block of opened.doc.blockSuiteDoc.getBlocksByFlavour(
        this.blockFlavour
      )) {
        if (closed) return;
        await this.waitForDatabaseSchema(block, controller.signal);
      }
      if (
        !(await this.guardService.can('Doc_Read', docId)) ||
        (permission === 'write' &&
          !(await this.guardService.can('Doc_Update', docId)))
      ) {
        throw new Error('Database access is no longer available.');
      }
      if (!closed) this.refresh();
    })().catch(error => {
      release();
      throw error;
    });
    return { ready, release };
  }

  /** An explicit selection opens just its host and schema, never all row shards. */
  async resolveDatabaseSelection(
    database: DatabaseInfo,
    permission: 'read' | 'write'
  ): Promise<DatabaseInfo> {
    const assertAccess = async () => {
      if (
        !(await this.guardService.can('Doc_Read', database.docId)) ||
        (permission === 'write' &&
          !(await this.guardService.can('Doc_Update', database.docId)))
      ) {
        throw new Error('Database access is no longer available.');
      }
    };
    await assertAccess();
    const record = this.docsService.list.doc$(database.docId).value;
    if (!record || record.trash$.value)
      throw new Error('Database document is unavailable.');
    const { doc, release } = this.docsService.open(database.docId);
    try {
      await doc.waitForSyncReady();
      const block = doc.blockSuiteDoc.getBlock(database.blockId);
      if (!block || block.model.flavour !== this.blockFlavour)
        throw new Error('Database is unavailable.');
      await this.waitForDatabaseSchema(block);
      await assertAccess();
      const currentRecord = this.docsService.list.doc$(database.docId).value;
      const currentBlock = doc.blockSuiteDoc.getBlock(database.blockId);
      if (
        !currentRecord ||
        currentRecord.trash$.value ||
        currentBlock?.model !== block.model
      ) {
        throw new Error('Database is unavailable.');
      }
      const selected = this.extractBlockInfo(currentBlock, currentRecord);
      if (!selected || !isAccessibleWorkspaceDatabase(selected))
        throw new Error('Database is unavailable.');
      return selected;
    } finally {
      release();
    }
  }

  private async waitForDatabaseSchema(
    block: Block,
    signal?: AbortSignal
  ): Promise<void> {
    signal?.throwIfAborted();
    if (isDatabaseStorageModelReady(block.model as any)) return;
    const rootDocId = (block.model.props as { databaseRootDocId?: string })
      .databaseRootDocId;
    const releaseRoot = rootDocId
      ? preloadDatabaseSubdocsForDataSourceRefs(
          [{ databaseRootDocId: rootDocId }],
          this.workspaceService
        )
      : undefined;
    try {
      if (rootDocId)
        getSystemWorkspaceStore(block.model.store, rootDocId).load();
      await new Promise<void>((resolve, reject) => {
        let dispose: (() => void) | undefined;
        let settled = false;
        const finish = (error?: Error) => {
          if (settled) return;
          settled = true;
          clearTimeout(timeout);
          dispose?.();
          signal?.removeEventListener('abort', abort);
          if (error) reject(error);
          else resolve();
        };
        const abort = () =>
          finish(new DOMException('Picker closed', 'AbortError'));
        const timeout = setTimeout(
          () => finish(new Error('Database schema is unavailable.')),
          15_000
        );
        signal?.addEventListener('abort', abort, { once: true });
        dispose = observeDatabaseStorageModelReady(block.model as any, () =>
          finish()
        );
        if (settled) dispose();
        if (rootDocId)
          void this.workspaceService.workspace.engine.doc
            .waitForDocReady(rootDocId, signal)
            .then(
              () => {
                if (isDatabaseStorageModelReady(block.model as any)) finish();
              },
              error =>
                finish(
                  error instanceof Error ? error : new Error(String(error))
                )
            );
      });
    } finally {
      releaseRoot?.();
    }
  }

  /**
   * Directory part documents and the row shards of the loaded leading parts;
   * a corrupt directory names none.
   */
  private readDirectoryDocIds(host: DatabaseShardDirectoryHost): {
    partDocIds: string[];
    shardDocIds: string[];
  } {
    try {
      return (
        readDatabaseShardDirectoryDocIds(host) ?? {
          partDocIds: [],
          shardDocIds: [],
        }
      );
    } catch {
      return { partDocIds: [], shardDocIds: [] };
    }
  }

  /**
   * Group the workspace catalog, including indexed but unopened documents.
   */
  get databasesByDoc$(): LiveData<DatabaseDocGroup[]> {
    return this.databases$.map(databases =>
      this.groupDatabasesByDoc(databases)
    );
  }

  /**
   * Alias for canvasBlocks$ - keeps existing API working.
   * E018-S010: Canvas-only databases (filtered by xywh presence).
   */
  get canvasDatabases$(): LiveData<DatabaseInfo[]> {
    return this.canvasBlocks$;
  }

  /**
   * Alias for canvasBlocksByDoc$ - transforms to DatabaseDocGroup format.
   * E018-S010: Canvas databases grouped by doc.
   */
  get canvasDatabasesByDoc$(): LiveData<DatabaseDocGroup[]> {
    return this.canvasBlocksByDoc$.map(groups =>
      groups.map(
        (g): DatabaseDocGroup => ({
          docId: g.docId,
          docTitle: g.docTitle,
          databases: g.blocks,
        })
      )
    );
  }

  get userFacingCanvasDatabasesByDoc$(): LiveData<DatabaseDocGroup[]> {
    return this.canvasDatabasesByDoc$.map(groups =>
      groups
        .map(
          (g): DatabaseDocGroup => ({
            ...g,
            databases: g.databases.filter(isUserFacingActiveWorkspaceDatabase),
          })
        )
        .filter(g => g.databases.length > 0)
    );
  }

  // ============================================================
  // E018-S016: Workspace Trashcan
  // ============================================================

  /**
   * E018-S016: All trashed databases across workspace.
   * Filters blocks$ to only include items with isInTrash: true.
   */
  get trashcanDatabases$(): LiveData<DatabaseInfo[]> {
    return this.blocks$.map((databases: DatabaseInfo[]) =>
      databases.filter(db => db.isInTrash === true)
    );
  }

  /**
   * E018-S016: Trashed databases grouped by doc.
   */
  get trashcanDatabasesByDoc$(): LiveData<DatabaseDocGroup[]> {
    return this.trashcanDatabases$.map((databases: DatabaseInfo[]) => {
      const byDoc = new Map<
        string,
        { docTitle: string; databases: DatabaseInfo[] }
      >();

      for (const db of databases) {
        if (!byDoc.has(db.docId)) {
          byDoc.set(db.docId, {
            docTitle: db.docTitle,
            databases: [],
          });
        }
        byDoc.get(db.docId)!.databases.push(db);
      }

      return Array.from(byDoc.entries()).map(([docId, data]) => ({
        docId,
        docTitle: data.docTitle,
        databases: data.databases,
      }));
    });
  }

  // ============================================================
  // RBAC (Database-specific)
  // ============================================================

  /**
   * S020-8.20.4: Permission type for database filtering.
   * - 'read': Doc_Read permission (for Input Tab)
   * - 'write': Doc_Update permission (for Output Tab)
   */
  readonly accessibleDatabases$ = LiveData.from(
    watchPermittedDatabases(this.databases$, docId =>
      this.guardService.can$('Doc_Read', docId)
    ).pipe(map(databases => databases.filter(isAccessibleWorkspaceDatabase))),
    []
  );

  /**
   * E012-T018: Accessible databases grouped by doc.
   */
  readonly accessibleDatabasesByDoc$ = this.accessibleDatabases$.map(
    (databases: DatabaseInfo[]) => {
      return this.groupDatabasesByDoc(databases);
    }
  );

  /**
   * S020-8.20.4: Writable databases - only shows DBs in docs the user can write.
   * Used by Output Tab to filter target table selection.
   */
  readonly writableDatabases$ = LiveData.from(
    watchPermittedDatabases(
      this.databases$,
      docId => this.guardService.can$('Doc_Read', docId),
      docId => this.guardService.can$('Doc_Update', docId)
    ).pipe(map(databases => databases.filter(isAccessibleWorkspaceDatabase))),
    []
  );

  /**
   * S020-8.20.4: Writable databases grouped by doc.
   */
  readonly writableDatabasesByDoc$ = this.writableDatabases$.map(
    (databases: DatabaseInfo[]) => {
      return this.groupDatabasesByDoc(databases);
    }
  );

  /**
   * E018-S016: RBAC-filtered trashed databases.
   * Only shows trashed DBs in docs the user can read.
   */
  readonly accessibleTrashcanDatabases$ = LiveData.from(
    watchPermittedDatabases(this.trashcanDatabases$, docId =>
      this.guardService.can$('Doc_Read', docId)
    ),
    []
  );

  /**
   * S020-8.20.4: Helper to group databases by doc (DRY).
   */
  private groupDatabasesByDoc(databases: DatabaseInfo[]): DatabaseDocGroup[] {
    const byDoc = new Map<
      string,
      { docTitle: string; databases: DatabaseInfo[] }
    >();

    for (const db of sortDatabaseCatalog(databases)) {
      if (!byDoc.has(db.docId)) {
        byDoc.set(db.docId, {
          docTitle: db.docTitle,
          databases: [],
        });
      }
      byDoc.get(db.docId)!.databases.push(db);
    }

    return Array.from(byDoc.entries()).map(([docId, data]) => ({
      docId,
      docTitle: data.docTitle,
      databases: data.databases,
    }));
  }

  invalidatePermissionCache(docId?: string): void {
    this.guardService.revalidateCachedPermissions(
      docId ? { docIds: [docId] } : undefined
    );
    this.refresh();
  }

  // ============================================================
  // Database-specific Methods
  // ============================================================

  /**
   * The directory documents of a database as its loaded host stores them
   * now: the part documents and the row shards of the loaded parts.
   * Undefined while the host document or its database block is not loaded.
   */
  readDatabaseDirectoryDocIds(
    docId: string,
    blockId: string
  ): { partDocIds: string[]; shardDocIds: string[] } | undefined {
    const loaded = this.docsService.loaded(docId);
    if (!loaded) return undefined;
    try {
      const model = loaded.doc.blockSuiteDoc?.getBlock(blockId)?.model;
      if (model?.flavour !== 'affine:database') return undefined;
      return this.readDirectoryDocIds(
        resolveDatabaseStorageModel(model as any)
      );
    } finally {
      loaded.release();
    }
  }

  /**
   * Find a database by blockId.
   * Alias for findBlock() for backward compatibility.
   */
  findDatabase(blockId: string): DatabaseInfo | undefined {
    return this.findBlock(blockId);
  }

  /**
   * LiveData for a specific database's info.
   * Reactively updates when the database's title or columns change.
   */
  watchDatabase(blockId: string): LiveData<DatabaseInfo | undefined> {
    return this.blocks$.map((databases: DatabaseInfo[]) =>
      databases.find((db: DatabaseInfo) => db.blockId === blockId)
    );
  }

  /**
   * Finds all databases that have a relation pointing TO the given database.
   * Used for bidirectional ghost creation.
   */
  findIncomingRelations(targetDatabaseId: string): DatabaseInfo[] {
    return this.blocks$.value.filter((db: DatabaseInfo) =>
      db.columns.some(
        col =>
          col.type === 'relation' && col.targetDatabaseId === targetDatabaseId
      )
    );
  }

  // ============================================================
  // Automation-related Methods (will move to AutomationDiscoveryService in T004)
  // ============================================================

  /**
   * Finds all automations that reference a given database.
   * @deprecated Will move to AutomationDiscoveryService
   */
  findAutomationsReferencingDatabase(
    databaseBlockId: string
  ): Array<{ docId: string; blockId: string }> {
    const result: Array<{ docId: string; blockId: string }> = [];

    try {
      const docs = this.docsService.list.docs$.value;

      for (const docRecord of docs) {
        if (docRecord.trash$.value) continue;
        // Skip system docs (property stores, workspace meta) — they have no BlockSuite blocks
        if (docRecord.id.includes('$')) continue;

        try {
          const { doc, release } = this.docsService.open(docRecord.id);

          try {
            const blockSuiteDoc = doc.blockSuiteDoc;
            if (!blockSuiteDoc) continue;

            const automationBlocks =
              blockSuiteDoc.getBlocksByFlavour('affine:automation');

            for (const block of automationBlocks) {
              const props = block.model?.props as {
                sourceTableId?: string;
                inputTables?: Array<{ tableId?: string }>;
                targetTableId?: string;
                outputTables?: Array<{ tableId?: string }>;
              };

              const inputTableIds = new Set<string>();
              if (props?.sourceTableId) inputTableIds.add(props.sourceTableId);
              for (const input of props?.inputTables ?? []) {
                if (input.tableId) inputTableIds.add(input.tableId);
              }

              const outputTableIds = new Set<string>();
              if (props?.targetTableId) outputTableIds.add(props.targetTableId);
              for (const output of props?.outputTables ?? []) {
                if (output.tableId) outputTableIds.add(output.tableId);
              }

              if (
                inputTableIds.has(databaseBlockId) ||
                outputTableIds.has(databaseBlockId)
              ) {
                result.push({
                  docId: docRecord.id,
                  blockId: block.model?.id ?? block.id,
                });
              }
            }
          } finally {
            release();
          }
        } catch {
          // Doc not yet loaded/synced — will be picked up on next scan cycle
        }
      }
    } catch (e) {
      console.warn('[DatabaseDiscovery] Failed to find automations:', e);
    }

    return result;
  }

  /**
   * Finds all automations from OTHER docs that reference a given database.
   * @deprecated Will move to AutomationDiscoveryService
   */
  findIncomingAutomations(
    targetDatabaseId: string,
    currentDocId: string
  ): Array<{
    automationId: string;
    automationName: string;
    docId: string;
    docTitle: string;
    direction: 'source' | 'target';
    localDatabaseId: string; // E018-S014: The local DB that is referenced
  }> {
    const result: Array<{
      automationId: string;
      automationName: string;
      docId: string;
      docTitle: string;
      direction: 'source' | 'target';
      localDatabaseId: string;
    }> = [];

    try {
      const docs = this.docsService.list.docs$.value;

      for (const docRecord of docs) {
        if (docRecord.trash$.value) continue;
        if (docRecord.id === currentDocId) continue;
        // Skip system docs (property stores, workspace meta) — they have no BlockSuite blocks
        if (docRecord.id.includes('$')) continue;

        try {
          const { doc, release } = this.docsService.open(docRecord.id);

          try {
            const blockSuiteDoc = doc.blockSuiteDoc;
            if (!blockSuiteDoc) continue;

            const automationBlocks =
              blockSuiteDoc.getBlocksByFlavour('affine:automation');

            for (const block of automationBlocks) {
              const props = block.model?.props as {
                sourceTableId?: string;
                inputTables?: Array<{ tableId?: string }>;
                targetTableId?: string;
                outputTables?: Array<{ tableId?: string }>;
                title?: string;
              };

              const automationId = block.model?.id ?? block.id;
              const automationName = props?.title || 'Automation';
              const docTitle = docRecord.title$.value || 'Untitled';
              const inputTableIds = new Set<string>();
              if (props?.sourceTableId) inputTableIds.add(props.sourceTableId);
              for (const input of props?.inputTables ?? []) {
                if (input.tableId) inputTableIds.add(input.tableId);
              }

              const outputTableIds = new Set<string>();
              if (props?.targetTableId) outputTableIds.add(props.targetTableId);
              for (const output of props?.outputTables ?? []) {
                if (output.tableId) outputTableIds.add(output.tableId);
              }

              if (inputTableIds.has(targetDatabaseId)) {
                result.push({
                  automationId,
                  automationName,
                  docId: docRecord.id,
                  docTitle,
                  direction: 'source',
                  localDatabaseId: targetDatabaseId, // E018-S014
                });
              }

              if (outputTableIds.has(targetDatabaseId)) {
                result.push({
                  automationId,
                  automationName,
                  docId: docRecord.id,
                  docTitle,
                  direction: 'target',
                  localDatabaseId: targetDatabaseId, // E018-S014
                });
              }
            }
          } finally {
            release();
          }
        } catch {
          // Doc not yet loaded/synced — will be picked up on next scan cycle
        }
      }
    } catch (e) {
      console.warn(
        '[DatabaseDiscovery] Failed to find incoming automations:',
        e
      );
    }

    return result;
  }

  /**
   * Finds TRASHED automations in the SAME doc that reference a given database.
   * Used to create ghost nodes showing deleted automations that had connections.
   *
   * The trashcan stores items in props.items as an array of TrashcanItem:
   * - id: original block ID
   * - blockType: 'database' | 'automation'
   * - snapshot: serialized block data
   * - displayName: block name
   */
  findTrashedLocalAutomations(
    targetDatabaseId: string,
    currentDocId: string
  ): Array<{
    automationId: string;
    automationName: string;
    docId: string;
    docTitle: string;
    direction: 'source' | 'target';
    localDatabaseId: string;
    originalPosition?: { x: number; y: number; w: number; h: number };
  }> {
    const result: Array<{
      automationId: string;
      automationName: string;
      docId: string;
      docTitle: string;
      direction: 'source' | 'target';
      localDatabaseId: string;
      originalPosition?: { x: number; y: number; w: number; h: number };
    }> = [];

    try {
      const docRecord = this.docsService.list.doc$(currentDocId).value;
      if (!docRecord) return result;

      const { doc, release } = this.docsService.open(currentDocId);

      try {
        const blockSuiteDoc = doc.blockSuiteDoc;
        if (!blockSuiteDoc) return result;

        // Get the trashcan block (there's only one per doc)
        const trashcanBlocks =
          blockSuiteDoc.getBlocksByFlavour('affine:trashcan');

        if (trashcanBlocks.length === 0) return result;

        const trashcan = trashcanBlocks[0];
        const trashcanProps = trashcan.model?.props as {
          items?: Array<{
            id: string;
            blockType: 'database' | 'automation';
            snapshot: {
              content?: Array<{
                props?: {
                  sourceTableId?: string;
                  inputTables?: Array<{ tableId?: string }>;
                  targetTableId?: string;
                  outputTables?: Array<{ tableId?: string }>;
                  name?: string;
                };
              }>;
            };
            displayName?: string;
          }>;
        };

        const items = trashcanProps?.items ?? [];
        const docTitle = docRecord.title$.value || 'Untitled';

        for (const item of items) {
          // Only process automation items
          if (item.blockType !== 'automation') continue;

          // Extract props from snapshot
          // Snapshot structure: { content: [{ props: { sourceTableId, outputTables, title, xywh } }] }
          const snapshotContent = (
            item.snapshot as {
              content?: Array<{
                props?: {
                  sourceTableId?: string;
                  inputTables?: Array<{ tableId?: string }>;
                  outputTables?: Array<{ tableId: string }>;
                  targetTableId?: string;
                  title?: string;
                  xywh?: string;
                };
              }>;
            }
          )?.content?.[0];
          const snapshotProps = snapshotContent?.props;

          if (!snapshotProps) continue;

          const automationId = item.id;
          const automationName =
            item.displayName || snapshotProps.title || 'Automation';

          // Extract original position from xywh (Single Source: parseXywhToPosition)
          const originalPosition = parseXywhToPosition(snapshotProps.xywh);

          const inputTableIds = new Set<string>();
          if (snapshotProps.sourceTableId) {
            inputTableIds.add(snapshotProps.sourceTableId);
          }
          for (const input of snapshotProps.inputTables ?? []) {
            if (input.tableId) inputTableIds.add(input.tableId);
          }

          const outputTableIds = new Set<string>();
          if (snapshotProps.targetTableId) {
            outputTableIds.add(snapshotProps.targetTableId);
          }
          for (const output of snapshotProps.outputTables ?? []) {
            if (output.tableId) outputTableIds.add(output.tableId);
          }

          // Check if this trashed automation referenced our database as SOURCE (input)
          if (inputTableIds.has(targetDatabaseId)) {
            result.push({
              automationId,
              automationName,
              docId: currentDocId,
              docTitle,
              direction: 'source',
              localDatabaseId: targetDatabaseId,
              originalPosition,
            });
          }

          // Check if this trashed automation referenced our database as TARGET (output)
          if (outputTableIds.has(targetDatabaseId)) {
            result.push({
              automationId,
              automationName,
              docId: currentDocId,
              docTitle,
              direction: 'target',
              localDatabaseId: targetDatabaseId,
              originalPosition,
            });
          }
        }
      } finally {
        release();
      }
    } catch (e) {
      console.warn(
        '[DatabaseDiscovery] Failed to find trashed local automations:',
        e
      );
    }

    return result;
  }

  /**
   * Get the original position of a trashed block (database or automation).
   * Used to position ghosts at the location where the deleted block was.
   */
  getTrashedBlockPosition(
    blockId: string,
    currentDocId: string
  ): { x: number; y: number; w: number; h: number } | null {
    try {
      const { doc, release } = this.docsService.open(currentDocId);

      try {
        const blockSuiteDoc = doc.blockSuiteDoc;
        if (!blockSuiteDoc) return null;

        const trashcanBlocks =
          blockSuiteDoc.getBlocksByFlavour('affine:trashcan');
        if (trashcanBlocks.length === 0) return null;

        const trashcan = trashcanBlocks[0];
        const trashcanProps = trashcan.model?.props as {
          items?: Array<{
            id: string;
            snapshot: {
              content?: Array<{
                props?: {
                  xywh?: string;
                };
              }>;
            };
          }>;
        };

        const items = trashcanProps?.items ?? [];
        const item = items.find(i => i.id === blockId);

        if (!item) return null;

        const xywh = item.snapshot?.content?.[0]?.props?.xywh;
        if (!xywh) return null;

        const parsedXYWH = parseXYWH(xywh);
        if (!parsedXYWH) return null;
        const [x, y, w, h] = parsedXYWH;
        return { x, y, w, h };
      } finally {
        release();
      }
    } catch (e) {
      console.warn(
        '[DatabaseDiscovery] Failed to get trashed block position:',
        e
      );
      return null;
    }
  }
}
